import { AxiosError } from 'axios';
import { get, isObject, isString, isUndefined } from 'lodash-es';

import { hasText } from '@/utils/strings';

interface ServerError {
  code: string;
  message: string;
}

interface ServerResponse {
  error: ServerError;
}

function isAxiosError(error: unknown): error is AxiosError {
  return error instanceof Error && 'isAxiosError' in error;
}

function isServerResponse(error: unknown): error is ServerResponse {
  return isObject(error) && 'error' in error && !isUndefined(get(error, 'error.code')) && !isUndefined(get(error, 'error.message'));
}

/**
 * Best-effort machine-readable error code from an API failure, across BOTH server
 * envelope shapes: the standard `{ error: { code, message } }` (→ `error.code`) and
 * the looser `{ success: false, errorCode, message }` some endpoints return on a
 * 2xx soft-failure (→ `errorCode`). Returns `undefined` when no code is present.
 * Used by callers that branch on a specific code (e.g. the domain-connect flow).
 */
export function getErrorCode(error: unknown): string | undefined {
  const data = isAxiosError(error) ? error.response?.data : error;
  const code = get(data, 'errorCode') ?? get(data, 'error.code');
  return isString(code) ? code : undefined;
}

export function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    if (!isUndefined(error.response)) {
      if (isServerResponse(error.response.data)) {
        return `[${error.response.data.error.code}]: ${error.response.data.error.message}`;
      } else if (isObject(error.response.data)) {
        return JSON.stringify(error.response.data);
      } else if (isString(error.response.data)) {
        return error.response.data;
      } else {
        return `Server Error: ${error.response.status} ${error.response.statusText}`;
      }
    } else if (!isUndefined(error.request)) {
      return 'No response received from server';
    } else {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (hasText(error)) {
    return error;
  }

  return 'An unknown error occurred';
}
